import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import StandardModal from "../shared/Modal/StandardModal";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import {
  TextInput,
  Button,
  Group,
  Stack,
  Checkbox,
  Select,
  Text,
  Paper,
  Title,
  ActionIcon,
  Switch,
  Badge,
  Tooltip,
  Loader,
} from "@mantine/core";
import {
  IconUser,
  IconMinus,
  IconPlus,
  IconUsersGroup,
} from "@tabler/icons-react";

function AddGroupModal({
  isOpen,
  onClose,
  onGroupAdded,
  repTypes = [],
  loadingRepTypes = false,
}) {
  const { getVerifiedJWT } = useAuth();
  const [saving, setSaving] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [enableChat, setEnableChat] = useState(false);
  const [autoAdd, setAutoAdd] = useState(false);
  // const [includeDownline, setIncludeDownline] = useState(false);
  const [selectedRepType, setSelectedRepType] = useState("");
  const [nameError, setNameError] = useState("");
  const [autoAddError, setAutoAddError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isProcessingDownline, setIsProcessingDownline] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all available users (no group ID needed)
  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const jwt = await getVerifiedJWT();

      // Get all available users from new endpoint
      const response = await fetch(
        getApiEndpoint("/v1/groups/available-users"),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const users = (result.data || []).map(user => ({
          ...user,
          include_downline: false, // Default to false
        }));
        setAvailableUsers(sortUsersByName(users));
      }
    } catch (error) {
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setGroupName("");
      setEnableChat(false);
      setAutoAdd(false);
      setSelectedRepType("");
      setNameError("");
      setAutoAddError("");
      setSaveError("");
      setSelectedMembers([]);
      setAvailableUsers([]);
      setSearchQuery("");
      // Fetch users immediately when modal opens
      fetchAvailableUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Filter available users based on search query
  const filteredAvailableUsers = availableUsers.filter(user => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const fullName =
      `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
    const email = (user.email || "").toLowerCase();

    return fullName.includes(query) || email.includes(query);
  });

  // Sort users alphabetically
  const sortUsersByName = users => {
    return [...users].sort((a, b) => {
      const nameA = `${a.first_name || ""} ${a.last_name || ""}`
        .trim()
        .toLowerCase();
      const nameB = `${b.first_name || ""} ${b.last_name || ""}`
        .trim()
        .toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Get user display name
  const getUserDisplayName = user => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || "Unknown User";
  };

  // Handle adding member
  const handleAddMember = user => {
    setSelectedMembers(prev => sortUsersByName([...prev, user]));
    setAvailableUsers(prev =>
      sortUsersByName(
        prev.filter(u => u.user_season_id !== user.user_season_id)
      )
    );
  };

  // Handle removing member
  const handleRemoveMember = user => {
    setAvailableUsers(prev => sortUsersByName([...prev, user]));
    setSelectedMembers(prev =>
      sortUsersByName(
        prev.filter(u => u.user_season_id !== user.user_season_id)
      )
    );
  };

  // Handle toggling include_downline for member
  const handleToggleIncludeDownline = async user => {
    const newValue = !user.include_downline;

    // INSTANT UI UPDATE - Toggle immediately
    setSelectedMembers(prev =>
      prev.map(u =>
        u.user_season_id === user.user_season_id
          ? { ...u, include_downline: newValue }
          : u
      )
    );

    // If enabling, fetch and add downline users
    if (newValue) {
      setIsProcessingDownline(true);

      try {
        const jwt = await getVerifiedJWT();
        const response = await fetch(
          getApiEndpoint(`/v1/company-downline/downline/${user.id}`),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const downlineUsers = (result.data || []).map(downlineUser => ({
            ...downlineUser,
            include_downline: false,
          }));

          // Add downline users to selected members if not already there
          const currentSelectedIds = selectedMembers.map(m => m.user_season_id);
          const newDownlineUsers = downlineUsers.filter(
            du => !currentSelectedIds.includes(du.user_season_id)
          );

          if (newDownlineUsers.length > 0) {
            // Remove from available and add to selected
            setSelectedMembers(prev =>
              sortUsersByName([...prev, ...newDownlineUsers])
            );
            setAvailableUsers(prev =>
              sortUsersByName(
                prev.filter(
                  u =>
                    !newDownlineUsers.some(
                      nd => nd.user_season_id === u.user_season_id
                    )
                )
              )
            );
          }
        }
      } catch (error) {
      } finally {
        setIsProcessingDownline(false);
      }
    }
  };

  const createGroup = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/groups/group"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name: groupName.trim(),
          enable_chat: enableChat,
          auto_add: autoAdd,
          ...(autoAdd && selectedRepType && { rep_type_id: selectedRepType }),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to create group. Please try again."
        );
      }
    } catch (error) {
      throw new Error("Failed to create group. Please try again.");
    }
  };

  const handleSave = async () => {
    // Clear previous errors
    setNameError("");
    setAutoAddError("");
    setSaveError("");

    // Validate group name
    if (!groupName.trim()) {
      setNameError("Group name is required");
      return;
    }

    // Validate auto add requirements
    if (autoAdd && !selectedRepType) {
      setAutoAddError("Please select a rep type");
      return;
    }

    try {
      setSaving(true);
      const newGroup = await createGroup();

      // Add selected members to the group in background
      if (selectedMembers.length > 0 && newGroup?.id) {
        const jwt = await getVerifiedJWT();

        selectedMembers.forEach(async member => {
          try {
            await fetch(getApiEndpoint("/v1/groups/group/user"), {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              body: JSON.stringify({
                group_id: newGroup.id,
                user_season_id: member.user_season_id,
                include: true,
                include_downline: member.include_downline,
              }),
            });
          } catch (err) {}
        });
      }

      onGroupAdded();
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAutoAddChange = checked => {
    setAutoAdd(checked);
    if (!checked) {
      setSelectedRepType("");
      setAutoAddError("");
    }
  };

  // Convert rep types to Select options format
  const repTypeOptions = repTypes.map(repType => ({
    value: repType.id.toString(),
    label: repType.name,
  }));

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="ADD NEW GROUP"
      footerLeft={<div></div>}
      footerRight={<div></div>}
    >
      <Stack gap="md">
        <TextInput
          label="GROUP NAME"
          value={groupName}
          onChange={event => {
            setGroupName(event.currentTarget.value);
            setNameError(""); // Clear error when user types
            setSaveError(""); // Clear save error when user types
          }}
          placeholder="Enter group name"
          disabled={saving}
          error={nameError}
          required
        />

        <Text size="sm" c="dimmed">
          These settings once enabled can't be disabled.
        </Text>

        <Checkbox
          label="Enable Chat"
          checked={enableChat}
          onChange={event => setEnableChat(event.currentTarget.checked)}
          disabled={saving}
        />

        <Checkbox
          label="Auto Add By Rep Type"
          checked={autoAdd}
          onChange={event => handleAutoAddChange(event.currentTarget.checked)}
          disabled={saving}
        />

        {autoAdd && (
          <Stack gap="md">
            <Select
              label="REP TYPE"
              placeholder={
                loadingRepTypes ? "Loading rep types..." : "Select rep type"
              }
              data={repTypeOptions}
              value={selectedRepType}
              onChange={value => {
                setSelectedRepType(value);
                setAutoAddError(""); // Clear error when user selects
                setSaveError(""); // Clear save error when user selects
              }}
              disabled={saving || loadingRepTypes}
              searchable
              clearable
              nothingFoundMessage={
                repTypes.length === 0 && !loadingRepTypes
                  ? "No rep types available"
                  : "No options found"
              }
            />

            {autoAddError && (
              <DisplayMessage type="error" message={autoAddError} />
            )}
          </Stack>
        )}

        {saveError && <DisplayMessage type="error" message={saveError} />}

        {/* Member Selection Section */}
        {loadingUsers && (
          <Group justify="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading users...
            </Text>
          </Group>
        )}

        {!loadingUsers && (
          <>
            {/* Selected Members */}
            {selectedMembers.length > 0 && (
              <Paper p="md" withBorder>
                <Group justify="space-between" align="center" mb="md">
                  <Group gap="xs">
                    <Title order={5}>
                      Selected Members ({selectedMembers.length})
                    </Title>
                    {isProcessingDownline && <Loader size="xs" color="grape" />}
                  </Group>
                  <Text size="xs" c="dimmed" fw={500}>
                    Include Downline
                  </Text>
                </Group>
                <Stack gap="sm">
                  {selectedMembers.map(user => (
                    <Group
                      key={user.user_season_id}
                      gap="xs"
                      p="xs"
                      style={{
                        borderRadius: "6px",
                        backgroundColor: "var(--mantine-color-gray-0)",
                      }}
                    >
                      <IconUser size={16} color="var(--mantine-color-blue-6)" />
                      <Stack gap={1} style={{ flex: 1 }}>
                        <Group gap="xs">
                          <Text fw={500} size="sm">
                            {getUserDisplayName(user)}
                          </Text>
                          {user.include_downline && (
                            <Badge
                              size="xs"
                              color="grape"
                              variant="light"
                              leftSection={<IconUsersGroup size={10} />}
                            >
                              Includes Downline
                            </Badge>
                          )}
                        </Group>
                        {user.email && (
                          <Text size="xs" c="dimmed">
                            {user.email}
                          </Text>
                        )}
                      </Stack>
                      <Tooltip
                        label={
                          user.include_downline
                            ? "Disable auto-add downline"
                            : "Enable auto-add downline"
                        }
                        position="left"
                      >
                        <Switch
                          size="sm"
                          checked={user.include_downline || false}
                          onChange={() => handleToggleIncludeDownline(user)}
                          disabled={saving}
                          color="grape"
                        />
                      </Tooltip>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="red"
                        onClick={() => handleRemoveMember(user)}
                        disabled={saving}
                      >
                        <IconMinus size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Available Users */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Title order={5}>
                    Available Users ({filteredAvailableUsers.length})
                  </Title>
                  <TextInput
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={event =>
                      setSearchQuery(event.currentTarget.value)
                    }
                    size="xs"
                    style={{ width: "200px" }}
                  />
                </Group>
                {filteredAvailableUsers.length > 0 ? (
                  <Stack
                    gap="sm"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {filteredAvailableUsers.map(user => (
                      <Group
                        key={user.user_season_id}
                        gap="xs"
                        p="xs"
                        style={{
                          borderRadius: "6px",
                          backgroundColor: "var(--mantine-color-gray-0)",
                        }}
                      >
                        <IconUser
                          size={16}
                          color="var(--mantine-color-gray-6)"
                        />
                        <Stack gap={1} style={{ flex: 1 }}>
                          <Text fw={500} size="sm">
                            {getUserDisplayName(user)}
                          </Text>
                          {user.email && (
                            <Text size="xs" c="dimmed">
                              {user.email}
                            </Text>
                          )}
                        </Stack>
                        <ActionIcon
                          size="sm"
                          variant="light"
                          color="green"
                          onClick={() => handleAddMember(user)}
                          disabled={saving}
                        >
                          <IconPlus size={14} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                ) : searchQuery.trim() ? (
                  <Text c="dimmed" size="sm" ta="center" py="md" fs="italic">
                    No users match "{searchQuery}"
                  </Text>
                ) : (
                  <Text c="dimmed" size="sm" ta="center" py="md" fs="italic">
                    No users available
                  </Text>
                )}
              </Stack>
            </Paper>
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={!groupName.trim() || (autoAdd && !selectedRepType)}
          >
            {saving ? "Creating..." : "Create Group"}
          </Button>
        </Group>
      </Stack>
    </StandardModal>
  );
}

export default AddGroupModal;
